import { Button } from '../../components/Button';
import { Title } from '@portal_components/components/Title';
import { Booking, getBookingsErrorType, getBookingsByMpanSuccessType, getSiteSuccessType } from '../../../../APIDocs/apiTypes';
import { Tag } from '../../components/Tag';
import { useCallback, useMemo, useState } from 'react';
import { Typography } from '../../components/Typography';
import { Modal } from '../../components/Modal';
import { ListItem } from '../../components/ListItem';
import { Logger } from '../../Logger';
import { List } from '../../components/List';
import { contractTypeCodes, RestTypes, useJbpQuery } from '../../hooks/useJbpQuery';
import { JobHistoryLoading } from '../loadingElements/JobHistoryLoading';
import { SiteTypeUpdateModal } from '../SiteTypeUpdateModal';
import { useNavigate } from 'react-router-dom';
import { useUserInfo } from '@/hooks/useUserInfo';
import { useAppSelector } from '@/store';
import { EServiceLine } from '@/reducers/jbpGeneralSlice';
import { getFeedbackColourForBookingStatus } from '@/utils/getFeedbackColour';
import type { ReactElement } from 'react';

export interface props {
  className?: string;
  mpan: string;
  serviceLine: string;
  dataErrorOccurred: boolean;
  siteData: getSiteSuccessType | undefined;
  loading: boolean;
}

export const JobHistory = (props: props) => {
  const navigate = useNavigate();
  const { isInternalUser, canAccess } = useUserInfo();
  const { contractId, site } = useAppSelector(state => state.jbp);

  const now = new Date();
  const todayMinMillis = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0).getTime();
  const todayMaxMillis = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 0).getTime();

  const [bookingDataErrorOccurred, setBookingDataErrorOccurred] = useState(false);
  const [showSiteTypeConfirmationModal, setShowSiteTypeConfirmationModal] = useState(false);
  const [showNoActiveMopModal, setShowNoActiveMopModal] = useState(false);
  const [noActiveMopModelClosedByUser, setNoActiveMopModelClosedByUser] = useState(false);
  const activeMopForSite = props.siteData?.ActiveMopAppointment;

  if (showNoActiveMopModal == false && activeMopForSite === false && isInternalUser && noActiveMopModelClosedByUser === false) {
    setShowNoActiveMopModal(true);
  }

  const onBookingDataError = (err: getBookingsErrorType) => {
    Logger.Log(`Data found to not exist -> ${err?.msg}`);
    setBookingDataErrorOccurred(true);
  };

  const { data, isLoading } = useJbpQuery<getBookingsByMpanSuccessType, getBookingsErrorType>(
    RestTypes.Get,
    undefined,
    [`get-booking-list-${props.mpan}-${props.serviceLine}`],
    `jbp/${props.mpan}/booking`,
    true,
    props.serviceLine,
    undefined,
    onBookingDataError,
    600000,
    600000
  );

  const FILTERS = [
    { key: 'futureJobs', label: 'Future Jobs' },
    { key: 'pastJobs', label: 'Past Jobs' },
    { key: 'todaysJobs', label: "Today's Jobs" },
    { key: 'scheduledJobs', label: 'Scheduled' },
    { key: 'abortedJobs', label: 'Aborted' },
    { key: 'cancelledJobs', label: 'Cancelled' },
    { key: 'completedJobs', label: 'Completed' }
  ] as const;

  type FilterState = {
    [K in (typeof FILTERS)[number]['key']]: boolean;
  };

  const [filters, setFilters] = useState<FilterState>({
    futureJobs: false,
    pastJobs: false,
    todaysJobs: false,
    scheduledJobs: false,
    abortedJobs: false,
    cancelledJobs: false,
    completedJobs: false
  });

  const [futureJobs, setFutureJobs] = useState<Booking[]>([]);

  const shouldDisplayBooking = useCallback(
    (booking: Booking) => {
      const { pastJobs, todaysJobs, futureJobs, scheduledJobs, abortedJobs, cancelledJobs, completedJobs } = filters;

      const ignoreTimeBoundFilters = !pastJobs && !todaysJobs && !futureJobs;
      const ignoreStatusFilters = !scheduledJobs && !abortedJobs && !cancelledJobs && !completedJobs;

      if (ignoreStatusFilters && ignoreTimeBoundFilters) return true;

      if (!ignoreStatusFilters) {
        const statusMap: Record<string, boolean> = {
          Aborted: abortedJobs,
          Cancelled: cancelledJobs,
          Scheduled: scheduledJobs,
          Completed: completedJobs
        };
        if (statusMap[booking.Status] === false) return false;
      }

      if (booking.StartDate === undefined) {
        return false;
      }

      if (!ignoreTimeBoundFilters) {
        if (booking.StartDate >= todayMinMillis && booking.StartDate <= todayMaxMillis && !todaysJobs) return false;
        if (booking.StartDate < todayMinMillis && !pastJobs) return false;
        if (booking.StartDate > todayMaxMillis && !futureJobs) return false;
      }

      return true;
    },
    [filters, todayMaxMillis, todayMinMillis]
  );

  const getFutureJobs = (): Booking[] => {
    return (data ?? []).filter((booking: Booking) => booking.StartDate != undefined && booking.StartDate > todayMaxMillis && booking.Status === 'Scheduled');
  };

  const bookJobOnClick = () => {
    const futureJobs = getFutureJobs();

    if (futureJobs.length > 0) {
      setFutureJobs(futureJobs);
    } else {
      setShowSiteTypeConfirmationModal(true);
    }
  };

  const scheduledJobWarningCancelClicked = () => {
    setFutureJobs([]);
  };

  const onJobClicked = (booking: Booking) => {
    const serviceLine = Object.entries(contractTypeCodes).find(([, value]) => value === booking.ContractTypeId);
    if (serviceLine == undefined) return; // this should never happen
    navigate(`/job/${booking.BookingId}/${props.mpan}/${serviceLine[0]}/${booking.ContractId}`, {
      state: { activeMopAppointment: activeMopForSite, bookingData: booking }
    });
  };

  const confirmFutureJob = () => {
    setFutureJobs([]);
    setShowSiteTypeConfirmationModal(true);
  };

  const closeNoActiveMopModel = () => {
    setShowNoActiveMopModal(false);
    setNoActiveMopModelClosedByUser(true);
  };

  const title = 'Job History:';

  const { jobsForContractOrServiceLine, otherBookings } = useMemo(() => {
    if (!data || data.length === 0) {
      return { jobsForContractOrServiceLine: [], otherBookings: [] };
    }

    return data.reduce(
      (acc, booking) => {
        if (!shouldDisplayBooking(booking)) return acc;

        const isForSelectedContractOrServiceLine = (contractId == null || booking.ContractId === contractId) && booking.ContractTypeId == contractTypeCodes[props.serviceLine];

        if (isForSelectedContractOrServiceLine) {
          acc.jobsForContractOrServiceLine.push(booking);
        } else {
          acc.otherBookings.push(booking);
        }

        return acc;
      },
      {
        jobsForContractOrServiceLine: [] as Booking[],
        otherBookings: [] as Booking[]
      }
    );
  }, [data, props.serviceLine, contractId, shouldDisplayBooking]);

  const renderHistory = useMemo(() => !props.dataErrorOccurred && !bookingDataErrorOccurred && !isLoading, [props.dataErrorOccurred, bookingDataErrorOccurred, isLoading]);

  const renderHistoryListItem = (booking: Booking, index: number) => {
    const feedbackColour = getFeedbackColourForBookingStatus(booking.Status);
    const cells = isInternalUser ? [booking.JobTypeDesc, `${booking.ContractTypeDesc} - ${booking.CustomerGroupName}`] : [booking.JobTypeDesc];
    return (
      <ListItem key={index} title="Scheduled Date:" subTitle={booking.ScheduledDate} cells={cells} feedbackColour={feedbackColour} tagText={booking.Status} onClick={() => onJobClicked(booking)} />
    );
  };

  return (
    <div className="bg-white shadow rounded p-4">
      {props.loading ? (
        <JobHistoryLoading showTitle={true} className="w-full" />
      ) : (
        <div className={`${props?.className} flex flex-col gap-2`}>
          <div className={`flex justify-between`}>
            <Title text={title} className="mb-4 inline-block" />
            <div>
              <Button
                primary={true}
                text="Book Job"
                disabled={bookingDataErrorOccurred || isLoading || props.dataErrorOccurred || canAccess('canBook') === false || site?.bookable === false || activeMopForSite == false}
                icon={true}
                onClick={bookJobOnClick}
              />
            </div>
          </div>

          {!props.dataErrorOccurred && isLoading && <JobHistoryLoading className={props?.className}></JobHistoryLoading>}
          {bookingDataErrorOccurred && !isLoading && (
            <span>
              <Typography content="An error has occurred fetching the job history for this site. Please contact support" />
            </span>
          )}

          {renderHistory && (
            <div className="flex flex-wrap gap-2">
              {FILTERS.map(({ key, label }) => (
                <Tag key={key} title={label} initiallySelected={filters[key]} onClick={value => setFilters(prev => ({ ...prev, [key]: value }))} />
              ))}
            </div>
          )}

          {renderHistory &&
            (jobsForContractOrServiceLine.length > 0 ? (
              <div>
                <List title={title} data={jobsForContractOrServiceLine} className={isInternalUser ? 'max-h-[50vh]' : 'flex-grow'}>
                  {jobsForContractOrServiceLine.map<ReactElement>(renderHistoryListItem)}
                </List>
              </div>
            ) : (
              <Typography
                content={
                  Object.values(filters).some(Boolean)
                    ? 'There is no job history related to the selected filters'
                    : 'There is no job history available to view. Once a job has been booked, it will be displayed here'
                }
              />
            ))}
          {renderHistory && isInternalUser && otherBookings.length > 0 ? (
            <div className="my-6 flex-grow">
              <Title text="Other Job History:" className="inline-block" />
              <List title={title} data={otherBookings} className="max-h-[50vh]">
                {otherBookings.map<ReactElement>(renderHistoryListItem)}
              </List>
            </div>
          ) : (
            <span>{isInternalUser && <Typography content="There is no job history related to other contracts/service lines" />}</span>
          )}

          {futureJobs.length > 0 && (
            <Modal title="A booking already exists">
              <Typography content={`Are you sure you wish to book the job? The following booking(s) already exists:`} className="mb-3" />
              <>
                {futureJobs.map<ReactElement>((futureJob: Booking, index: number) => {
                  const customerInfo = isInternalUser ? ` (${futureJob.ContractTypeDesc} - ${futureJob.CustomerGroupName})` : '';
                  return <Typography content={`- ${futureJob.JobTypeDesc} scheduled for ${futureJob.ScheduledDate}${customerInfo}`} key={index} />;
                })}
              </>
              <div className="float-right">
                <Button primary={false} text="Cancel" className="mr-4 mt-5" disabled={false} onClick={scheduledJobWarningCancelClicked} />
                <Button
                  primary={false}
                  text="Ok"
                  disabled={false}
                  onClick={() => {
                    confirmFutureJob();
                  }}
                />
              </div>
            </Modal>
          )}

          {showSiteTypeConfirmationModal && (
            <SiteTypeUpdateModal mpan={props.mpan} serviceLine={props.serviceLine as EServiceLine} closeModalFunction={setShowSiteTypeConfirmationModal}></SiteTypeUpdateModal>
          )}

          {showNoActiveMopModal && (
            <Modal title="No active MOP appointment">
              <>
                {props.siteData?.NoActiveMopAppointmentReason === 'NO_MATCHING_MPID' && (
                  <Typography
                    content="A MOP Appointment has been identified from an MPID which is not a valid MPID for this customer. You can view site details and history, however you will not be able to book a job."
                    className="mb-3"
                  />
                )}
                {props.siteData?.NoActiveMopAppointmentReason === 'NO_ACTIVE_APPOINTMENT' && (
                  <Typography content="There is no active MOP for this site. You can view site details and history, however you will not be able to book a job." className="mb-3" />
                )}
                {props.siteData?.NoActiveMopAppointmentReason === 'INVALID_HOST_MPID' && (
                  <Typography
                    content="There is an active appointment for this site but is associated with a different service line. You can still view site details and history, however you will not be able to book a job"
                    className="mb-3"
                  />
                )}
              </>
              <div className="float-right">
                <Button primary={false} text="Ok" disabled={false} onClick={closeNoActiveMopModel} />
              </div>
            </Modal>
          )}
        </div>
      )}
    </div>
  );
};
